import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      section.querySelectorAll('[data-animate]').forEach((el) => {
        (el as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen-dynamic flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <video
          src="https://public.readdy.ai/ai/video_res/019fb261-a689-7e53-b46c-94cd89f14540.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 via-primary-900/30 to-primary-900/60"></div>
      </div>

      <div className="relative z-10 section-container text-center w-full py-24 md:py-0">
        <div className="max-w-3xl mx-auto">
          <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
            <span className="inline-block px-4 py-1.5 text-xs font-label bg-white/15 text-white rounded-full backdrop-blur-sm border border-white/20 mb-6">
              未境原創 · 愛情小說出版
            </span>
          </div>

          <h1
            data-animate
            className="opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white leading-tight mb-4 text-shadow-hero"
          >
            未境原創 VIJING
          </h1>

          <p
            data-animate
            className="opacity-0 translate-y-6 transition-all duration-700 text-lg md:text-xl lg:text-2xl font-heading font-medium text-white/95 leading-snug mb-6 text-shadow-hero"
          >
            在故事未竟之處，
            <br className="hidden md:block" />
            遇見愛與成長的另一種可能
          </p>

          <p
            data-animate
            className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed mb-4 font-body text-shadow-hero"
          >
            未境原創 VIJING 以愛情小說為主要出版方向，透過人物關係、情感選擇與成長歷程，帶領讀者走進愛、失去、重逢與自我理解交織的故事世界。
          </p>

          <p
            data-animate
            className="opacity-0 translate-y-6 transition-all duration-700 text-sm text-accent-300 font-heading italic mb-10 text-shadow-hero"
          >
            從心動與陪伴，到遺憾、選擇與命運重逢，每一段故事都留下尚未抵達的情感境地。
          </p>

          <div
            data-animate
            className="opacity-0 translate-y-6 transition-all duration-700 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Link
              to="/about"
              className="px-6 py-3 text-sm font-label bg-white text-primary-800 rounded-full hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
            >
              認識未境原創
            </Link>
            <Link
              to="/stores"
              className="px-6 py-3 text-sm font-label bg-white/15 text-white border border-white/30 rounded-full hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              查看合作購書通路
            </Link>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="px-6 py-3 text-sm font-label bg-white/15 text-white border border-white/30 rounded-full hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              查詢完整書目
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}